"use client";

// The dashboard's two bar lists — the Pipeline breakdown and the Application
// funnel — in one module because they are one device: a labelled row whose
// track fills to a value on mount, staggered by row position so the list reads
// as filling in rather than switching on.
//
// TWO THINGS WORTH KNOWING ABOUT THE FILL.
//
// It is `scaleX`, not `width`. Animating width reflows the row on every frame
// and takes the fill off the compositor; scaleX is a transform and stays on
// it. The cost is that scaling would squash a rounded cap, so the radius lives
// on the TRACK (`overflow-hidden rounded-pill`) and the fill is square — the
// track clips those same rounded ends, so the bar is a pill at 3% and at 100%
// alike. This is the one transform this system allows on something that is not
// an entrance, and it earns it: a progress bar that is not filled by a
// transform is a progress bar that jitters.
//
// It runs for `slow`. A bar appearing at full width is a state, not a
// measurement — the length is the information, so the length is what gets
// drawn. 850ms is the same long band the gauge and the donut draw on, so every
// "this value is this big" gesture in the app is one gesture.
//
// Under reduced motion every fill is at its final width immediately, with no
// stagger — the numbers and the labels already carry the whole fact.

import { motion } from "framer-motion";
import { DUR_SLOW, EASE_SETTLE, stagger } from "@/app/components/motion";
import { cn } from "@/app/lib/cn";

interface ProgressRowProps {
  label: string;
  pct: number;
  /** Any CSS color — a series token, or a color-mix step of one. */
  color: string;
  reduced: boolean;
  index: number;
  /** Right-hand readout. Percentages for the pipeline, counts for the funnel. */
  value: string;
}

function ProgressBar({
  pct,
  color,
  reduced,
  index,
  className,
}: {
  pct: number;
  color: string;
  reduced: boolean;
  index: number;
  className?: string;
}) {
  return (
    <div className={cn("h-1.5 flex-1 overflow-hidden rounded-pill bg-muted", className)}>
      <motion.div
        className="h-full origin-left"
        style={{ background: color }}
        initial={{ scaleX: reduced ? pct / 100 : 0 }}
        animate={{ scaleX: pct / 100 }}
        transition={
          reduced
            ? { duration: 0.01 }
            : { duration: DUR_SLOW, ease: EASE_SETTLE, delay: stagger(index) }
        }
      />
    </div>
  );
}

// One row of the work-type breakdown: a dot in the series hue, the label, the
// track, and the share as a percentage.
export function PipelineRow({ label, pct, color, reduced, index, value }: ProgressRowProps) {
  return (
    <div className="flex items-center gap-3 py-2">
      <span aria-hidden="true" className="h-2 w-2 flex-none rounded-full" style={{ background: color }} />
      <span className="w-20 flex-none text-xs text-foreground">{label}</span>
      <ProgressBar pct={pct} color={color} reduced={reduced} index={index} />
      <span className="w-10 flex-none text-right text-xs font-semibold text-foreground tabular-nums">
        {value}
      </span>
    </div>
  );
}

// One row of the funnel. The bar gets progressively lighter down the stages —
// the process narrowing — via color-mix against the card, which keeps the ramp
// derived from --primary instead of being four literals that drift apart the
// next time the accent changes.
//
// The row itself is the hover target: a faint wash on the row box rather than
// on the bar, so the whole line responds and the bar's own length stays the
// only thing its color is saying.
export function FunnelRow({
  label,
  pct,
  mix,
  color,
  count,
  reduced,
  index,
}: {
  label: string;
  pct: number;
  /** Percentage of --primary to mix into the card for this stage's fill. */
  mix?: number;
  /** Raw fill color — the approved mockup's four-step blue ramp. Takes
   * precedence over `mix` when given, so a caller can pin the ramp exactly
   * instead of deriving it from --primary. */
  color?: string;
  count: number;
  reduced: boolean;
  index: number;
}) {
  const resolved = color ?? `color-mix(in srgb, var(--primary) ${mix}%, var(--card))`;

  return (
    <div className="flex items-center gap-3 rounded-md px-2 py-2 transition-colors duration-150 hover:bg-row-hover-wash">
      <span className="w-32 flex-none truncate text-xs text-foreground" title={label}>
        {label}
      </span>
      <ProgressBar pct={pct} color={resolved} reduced={reduced} index={index} />
      <span className="w-8 flex-none text-right text-xs font-semibold text-foreground tabular-nums">
        {count}
      </span>
    </div>
  );
}
